// hooks/useFilterParams.js
import { useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Hook centralizado para manejar filtros de búsqueda en la URL.
 *
 * Parámetros URL:
 *   q  = searchTerm (texto libre)
 *   r  = ramo_Id
 *   u  = rubro_Id
 *   su = subrubro_Id
 *   m  = marca_Id
 *   p  = page
 *   pp = perPage
 *   ob = orderBy
 *
 * Todos los valores en la URL son IDs numéricos (nunca descripciones).
 * Los componentes reciben los IDs y los usan directamente para llamar al backend.
 * El CategoryFilter resuelve ID → descripción solo para mostrar en el Select.
 */
export const useFilterParams = () => {
  const navigate          = useNavigate();
  const location          = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Leer desde URL ─────────────────────────────────────────────────────────
  const searchTerm       = searchParams.get('q')  || '';
  const selectedRamoId   = searchParams.get('r')  || '';
  const selectedRubroId  = searchParams.get('u')  || '';
  const selectedSubrubroId = searchParams.get('su') || '';
  const selectedMarcaId  = searchParams.get('m')  || '';
  const currentPage      = Number(searchParams.get('p'))  || 1;
  const perPage          = Number(searchParams.get('pp')) || 42;
  const orderBy          = searchParams.get('ob') || '';

  // ── Actualizar URL sin recargar ────────────────────────────────────────────
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);

      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          next.set(key, String(value));
        } else {
          next.delete(key);
        }
      });

      // Al cambiar filtros, resetear página
      if (Object.keys(updates).some(k => ['q','r','u','su','m','pp'].includes(k))) {
        next.delete('p');
      }

      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ── Setters individuales ───────────────────────────────────────────────────
  const setSearch      = (v) => updateParams({ q: v });
  const setRamoId      = (v) => updateParams({ r: v, u: '', su: '' }); // limpiar hijos
  const setRubroId     = (v) => updateParams({ u: v, su: '' });
  const setSubrubroId  = (v) => updateParams({ su: v });
  const setMarcaId     = (v) => updateParams({ m: v });
  const setCurrentPage = (v) => updateParams({ p: v });
  const setPerPage     = (v) => updateParams({ pp: v });
  const setOrderBy     = (v) => updateParams({ ob: v });

  // ── Limpiar todos los filtros ──────────────────────────────────────────────
  const clearAll = () => setSearchParams({}, { replace: true });

  return {
    // valores
    searchTerm,
    selectedRamoId,
    selectedRubroId,
    selectedSubrubroId,
    selectedMarcaId,
    currentPage,
    perPage,
    orderBy,

    // setters
    setSearch,
    setRamoId,
    setRubroId,
    setSubrubroId,
    setMarcaId,
    setCurrentPage,
    setPerPage,
    setOrderBy,
    updateParams,
    clearAll,
  };
};